import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAdminIssues, AdminIssue } from '@/hooks/useAdminIssues';
import { useIssueComments } from '@/hooks/useIssueComments';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { IssueForm } from './IssueForm';
import { Plus, Lightbulb, Bug, CheckSquare, ArrowLeft, Trash2, Edit, Send, MessageSquarePlus, X, Camera, AlertTriangle, Copy } from 'lucide-react';
import { generateLovablePrompt } from '@/utils/generateLovablePrompt';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { da, enUS } from 'date-fns/locale';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

interface IssueTrackerDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const IssueTrackerDrawer = ({ open, onOpenChange }: IssueTrackerDrawerProps) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('all');
  const [selectedIssue, setSelectedIssue] = useState<AdminIssue | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState<string>('');
  const [commentText, setCommentText] = useState('');

  const filters = activeTab === 'all' ? {} : 
    activeTab === 'mine' ? { createdBy: user?.id } :
    { status: activeTab };

  const { issues, createIssue, updateIssue, deleteIssue } = useAdminIssues(filters);
  const { comments, addComment } = useIssueComments(selectedIssue?.id);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'idea': return <Lightbulb className="h-4 w-4 text-primary" />;
      case 'bug': return <Bug className="h-4 w-4 text-destructive" />;
      case 'todo': return <CheckSquare className="h-4 w-4 text-accent" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-destructive';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-accent';
      case 'low': return 'bg-muted';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'not_started': return 'bg-muted';
      case 'in_progress': return 'bg-primary';
      case 'resolved': return 'bg-green-600';
      case 'archived': return 'bg-gray-500';
    }
  };

  const handleStatusChange = (status: string) => {
    if (status === 'resolved' || status === 'archived') {
      setNewStatus(status);
      setShowStatusDialog(true);
    } else {
      updateIssue({ 
        id: selectedIssue!.id, 
        updates: { status: status as any } 
      });
    }
  };

  const confirmStatusChange = () => {
    const updates: any = { status: newStatus };
    if (newStatus === 'resolved') {
      updates.resolved_at = new Date().toISOString();
    }
    updateIssue({ id: selectedIssue!.id, updates });
    setShowStatusDialog(false);
    setSelectedIssue(null);
  };

  const handleAddComment = () => {
    if (!commentText.trim() || !selectedIssue) return;
    
    addComment({
      issue_id: selectedIssue.id,
      comment: commentText,
      created_by: user!.id,
      created_by_email: user!.email || '',
    });
    setCommentText('');
  };

  const locale = i18n.language === 'dk' ? da : enUS;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="border-b pb-4 px-6 pt-6">
            <DialogTitle className="flex items-center justify-between">
              {selectedIssue ? (
                <Button variant="ghost" size="sm" onClick={() => setSelectedIssue(null)}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Tilbage
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <MessageSquarePlus className="h-5 w-5" />
                  <span className="text-xl font-bold">Issue Tracker</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                {!selectedIssue && (
                  <Button size="sm" onClick={() => setShowCreateDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Ny Issue
                  </Button>
                )}
                <DialogClose asChild>
                  <Button variant="ghost" size="icon">
                    <X className="h-4 w-4" />
                  </Button>
                </DialogClose>
              </div>
            </DialogTitle>
          </DialogHeader>

          <ScrollArea className="flex-1 px-6">
            {!selectedIssue ? (
              <>
                <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6 mt-4">
                  <TabsList className="grid w-full grid-cols-3 lg:grid-cols-5 gap-2 h-auto p-1">
                    <TabsTrigger value="all" className="flex-col gap-1 py-2">
                      <span className="text-xs font-medium">Alle</span>
                    </TabsTrigger>
                    <TabsTrigger value="mine" className="flex-col gap-1 py-2">
                      <span className="text-xs font-medium">Mine</span>
                    </TabsTrigger>
                    <TabsTrigger value="not_started" className="flex-col gap-1 py-2">
                      <span className="text-xs font-medium">Ikke startet</span>
                    </TabsTrigger>
                    <TabsTrigger value="in_progress" className="flex-col gap-1 py-2">
                      <span className="text-xs font-medium">I gang</span>
                    </TabsTrigger>
                    <TabsTrigger value="resolved" className="flex-col gap-1 py-2">
                      <span className="text-xs font-medium">Løst</span>
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                <div className="space-y-4 pb-6">
                  {issues.map((issue) => (
                    <div
                      key={issue.id}
                      className="p-5 border rounded-lg hover:bg-muted/50 cursor-pointer transition-all hover:shadow-sm"
                      onClick={() => setSelectedIssue(issue)}
                    >
                      <div className="flex items-start gap-4">
                        <div className="mt-0.5">{getTypeIcon(issue.type)}</div>
                        <div className="flex-1 min-w-0 space-y-3">
                          <h4 className="font-semibold text-base leading-tight">{issue.title}</h4>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={`${getStatusColor(issue.status)} text-white`} variant="secondary">
                              {issue.status === 'not_started' && 'Ikke startet'}
                              {issue.status === 'in_progress' && 'I gang'}
                              {issue.status === 'resolved' && 'Løst'}
                              {issue.status === 'archived' && 'Arkiveret'}
                            </Badge>
                            <Badge className={`${getPriorityColor(issue.priority)} text-white`} variant="secondary">
                              {issue.priority === 'low' && 'Lav'}
                              {issue.priority === 'medium' && 'Medium'}
                              {issue.priority === 'high' && 'Høj'}
                              {issue.priority === 'urgent' && 'Urgent'}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {issue.created_by_email} • {formatDistanceToNow(new Date(issue.created_at), { addSuffix: true, locale })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {issues.length === 0 && (
                    <div className="text-center py-12">
                      <p className="text-muted-foreground text-lg">Ingen issues fundet</p>
                      <p className="text-sm text-muted-foreground mt-2">Opret en ny issue for at komme i gang</p>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="space-y-6 pb-6 pt-4">
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="mt-1">{getTypeIcon(selectedIssue.type)}</div>
                    <h2 className="text-2xl font-bold leading-tight flex-1">{selectedIssue.title}</h2>
                  </div>

                  <div className="flex items-center gap-3 flex-wrap">
                    <Select value={selectedIssue.status} onValueChange={handleStatusChange}>
                      <SelectTrigger className="w-52">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="not_started">Ikke startet</SelectItem>
                        <SelectItem value="in_progress">I gang</SelectItem>
                        <SelectItem value="resolved">Løst</SelectItem>
                        <SelectItem value="archived">Arkiveret</SelectItem>
                      </SelectContent>
                    </Select>
                    <Badge className={`${getPriorityColor(selectedIssue.priority)} text-white text-sm px-3 py-1`}>
                      {selectedIssue.priority === 'low' && 'Lav prioritet'}
                      {selectedIssue.priority === 'medium' && 'Medium prioritet'}
                      {selectedIssue.priority === 'high' && 'Høj prioritet'}
                      {selectedIssue.priority === 'urgent' && 'Urgent'}
                    </Badge>
                  </div>

                  <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Oprettet:</span>
                      <span className="text-muted-foreground">{new Date(selectedIssue.created_at).toLocaleString('da-DK')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Af:</span>
                      <span className="text-muted-foreground">{selectedIssue.created_by_email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Side:</span>
                      <span className="text-muted-foreground truncate">{selectedIssue.page_title || selectedIssue.page_url}</span>
                    </div>
                  </div>

                  {selectedIssue.description && (
                    <div className="border-t pt-5">
                      <h3 className="font-semibold mb-3 text-base">Beskrivelse</h3>
                      <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">{selectedIssue.description}</p>
                    </div>
                  )}

                  {/* Screenshot Section */}
                  {selectedIssue.screenshot_url && (
                    <div className="border-t pt-5">
                      <div className="flex items-center gap-2 mb-3">
                        <Camera className="h-4 w-4 text-muted-foreground" />
                        <h3 className="font-semibold text-base">Screenshot</h3>
                      </div>
                      <a href={selectedIssue.screenshot_url} target="_blank" rel="noopener noreferrer">
                        <img 
                          src={selectedIssue.screenshot_url} 
                          alt="Issue screenshot" 
                          className="w-full rounded-md border hover:opacity-80 transition-opacity cursor-pointer"
                        />
                      </a>
                    </div>
                  )}

                  {/* Error Log Section */}
                  {selectedIssue.error_log && (
                    <div className="border-t pt-5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-destructive" />
                          <h3 className="font-semibold text-base">Error Logs ({selectedIssue.error_log.totalLogs || 0})</h3>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(JSON.stringify(selectedIssue.error_log, null, 2));
                            toast.success('Error logs kopieret');
                          }}
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          Copy
                        </Button>
                      </div>
                      <Accordion type="single" collapsible className="border rounded-md">
                        {selectedIssue.error_log.logs?.slice(0, 10).map((log: any, idx: number) => (
                          <AccordionItem key={idx} value={`log-${idx}`}>
                            <AccordionTrigger className="px-4 py-2 text-sm hover:no-underline">
                              <div className="flex items-center gap-2">
                                <Badge variant={log.level === 'error' ? 'destructive' : 'secondary'} className="text-xs">
                                  {log.level || log.type}
                                </Badge>
                                <span className="truncate max-w-md text-left">{log.message}</span>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-3">
                              <div className="space-y-2 text-xs">
                                <div>
                                  <span className="font-medium">Message:</span> {log.message}
                                </div>
                                {log.stack && (
                                  <div>
                                    <span className="font-medium">Stack:</span>
                                    <pre className="mt-1 p-2 bg-muted rounded overflow-x-auto text-xs">{log.stack}</pre>
                                  </div>
                                )}
                                {log.url && (
                                  <div>
                                    <span className="font-medium">URL:</span> {log.url}:{log.line}:{log.column}
                                  </div>
                                )}
                                <div>
                                  <span className="font-medium">Timestamp:</span> {new Date(log.timestamp).toLocaleString('da-DK')}
                                </div>
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                      {selectedIssue.error_log.logs?.length > 10 && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Viser 10 af {selectedIssue.error_log.totalLogs} logs
                        </p>
                      )}
                    </div>
                  )}

                  {/* Copy to Lovable Button */}
                  <div className="border-t pt-5">
                    <Button
                      onClick={() => {
                        const prompt = generateLovablePrompt(selectedIssue);
                        navigator.clipboard.writeText(prompt);
                        toast.success('🚀 Prompt kopieret! Indsæt i Lovable chat for at få hjælp.');
                      }}
                      variant="default"
                      className="w-full"
                      size="lg"
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      📋 Copy til Lovable
                    </Button>
                  </div>

                  {selectedIssue.created_by === user?.id && (
                    <div className="flex gap-2 pt-2">
                      <Button variant="destructive" size="sm" onClick={() => {
                        deleteIssue(selectedIssue.id);
                        setSelectedIssue(null);
                      }}>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Slet issue
                      </Button>
                    </div>
                  )}
                </div>

                <div className="border-t pt-5">
                  <h3 className="font-semibold mb-4 text-base flex items-center gap-2">
                    💬 Kommentarer <span className="text-muted-foreground font-normal">({comments.length})</span>
                  </h3>
                  
                  <div className="space-y-4 mb-5">
                    {comments.map((comment) => (
                      <div key={comment.id} className="border-l-2 border-primary pl-4 py-3 bg-muted/30 rounded-r">
                        <p className="text-xs text-muted-foreground mb-2">
                          <span className="font-medium">{comment.created_by_email}</span> • {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true, locale })}
                        </p>
                        <p className="whitespace-pre-wrap text-sm leading-relaxed">{comment.comment}</p>
                      </div>
                    ))}
                    {comments.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">Ingen kommentarer endnu. Vær den første til at kommentere!</p>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <Textarea
                      placeholder="Tilføj en kommentar..."
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      rows={3}
                      className="resize-none"
                    />
                    <Button size="icon" onClick={handleAddComment} className="shrink-0">
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Opret ny issue</DialogTitle>
          </DialogHeader>
          <IssueForm
            onSubmit={(data) => {
              createIssue(data);
              setShowCreateDialog(false);
            }}
            onCancel={() => setShowCreateDialog(false)}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <AlertDialogContent>
          <AlertDialogTitle>
            {newStatus === 'resolved' ? 'Marker som løst?' : 'Arkiver issue?'}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {newStatus === 'resolved' 
              ? 'Dette vil sætte resolved_at timestamp.'
              : 'Arkiverede issues kan stadig ses, men er skjult fra standard visning.'}
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuller</AlertDialogCancel>
            <AlertDialogAction onClick={confirmStatusChange}>Bekræft</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
